import { filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonVariant } from "@swivl-mono/ui";
import { AnimatePresence, motion } from "motion/react";
import { ReactNode, memo, useEffect, useRef, useState } from "react";
import { BsArrowLeft, BsXLg } from "react-icons/bs";

import useAutoFocus from "../../../Hooks/useAutoFocus";
import useKeyPressAction from "../../../Hooks/useKeyPressAction";

interface DetailsPanelProps {
  defaultTabIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  onTabChange?: (tab: string) => void;
  renderTab: (tab: string, navigateForward: (tab?: string) => void, isPanelOpen: boolean) => ReactNode;
  tabs: string[];
}

const BaseDetailsPanel = ({ defaultTabIndex = 0, onClose, onTabChange, isOpen, renderTab, tabs }: DetailsPanelProps) => {
  const [currentTabIndex, setCurrentTabIndex] = useState(defaultTabIndex);
  const [direction, setDirection] = useState(1);
  const panelRef = useRef<HTMLDivElement>(null);
  useAutoFocus(panelRef, [isOpen, currentTabIndex]);
  useKeyPressAction({
    callback: onClose,
    key: "Escape",
    targetEl: panelRef.current
  });

  useEffect(() => {
    if (!isOpen) {
      setDirection(1);
      setCurrentTabIndex(defaultTabIndex);
      return;
    }

    setCurrentTabIndex(defaultTabIndex);
  }, [defaultTabIndex, isOpen]);

  useEffect(() => {
    const currentTab = tabs[currentTabIndex];
    if (isOpen && currentTab) {
      onTabChange?.(currentTab);
    }
  }, [currentTabIndex, isOpen, onTabChange, tabs]);

  const handlePrevTab = () => {
    if (currentTabIndex > 0) {
      setDirection(-1);
      setCurrentTabIndex((prev) => prev - 1);
    } else {
      onClose();
    }
  };

  const handleNextTab = (tab?: string) => {
    if (tab) {
      const index = tabs.indexOf(tab);
      if (index > -1) {
        setDirection(1);
        setCurrentTabIndex(index);
        return;
      }
    }

    if (currentTabIndex < tabs.length - 1) {
      setDirection(1);
      setCurrentTabIndex((prev) => prev + 1);
    }
  };

  return (
    <div
      aria-expanded={isOpen}
      className={filterClasses(
        "fixed top-12 right-0 z-50 flex h-[calc(100%-48px)] w-1/3 flex-col overflow-auto bg-white px-4 py-4 shadow-[-4px_2px_8px_rgba(0,0,0,0.1)] transition-all duration-300 md:px-8",
        isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      )}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="mb-8 flex h-16 w-full items-center justify-between space-x-1 py-1.5 md:space-x-2">
        {/* Back Button */}
        <div className="flex w-1/4 justify-start">
          <Button leftIcon={<BsArrowLeft className="size-4 md:size-6" strokeWidth={0.4} />} variant={ButtonVariant.GHOST} onClick={handlePrevTab}>
            <span className="text-lg font-semibold">Back</span>
          </Button>
        </div>

        {/* Heading */}
        <div className="flex w-1/2 items-center justify-center overflow-x-hidden px-1.5">
          <h5 className="truncate font-semibold tracking-wide md:text-xl">{tabs[currentTabIndex]}</h5>
        </div>

        {/* Close Button */}
        <div className="flex w-1/4 justify-end">
          <Button aria-label="Close Panel" variant={ButtonVariant.GHOST} onClick={onClose}>
            <BsXLg strokeWidth={2} size={16} />
          </Button>
        </div>
      </div>

      {/* Tab */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          exit={{ x: -direction * 100, opacity: 0 }}
          initial={{ x: direction * 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          key={tabs[currentTabIndex]}
          className="flex min-h-0 flex-1 flex-col"
        >
          {renderTab(tabs[currentTabIndex], handleNextTab, isOpen)}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const DetailsPanel = memo(BaseDetailsPanel);
